import {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import CornerUpLeftDoubleIcon from "@redotech/redo-icons/corner-up-left-double.svg";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import FlipForwardIcon from "@redotech/redo-icons/flip-forward.svg";
import InfoCircleIcon from "@redotech/redo-icons/info-circle.svg";
import { SimpleEmailMessage } from "@redotech/redo-merchant-app-common/simple-email-message";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { BgColorValue } from "@redotech/redo-web/theme/box";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import { memo, useState } from "react";
import type { ReplyMode } from "../../hooks/use-reply-mode";
import { MessageInput } from "../message-input";

export interface EmailContact {
  name: string | null;
  email: string;
}

export interface FormattedEmailMessage {
  id: MessageId;
  messageId: MessageId;
  threadId: ChannelThreadId;
  senderName: string;
  senderInitials: string;
  isMessageFromCustomer: boolean;
  from: EmailContact;
  to: EmailContact[];
  cc: EmailContact[];
  bcc: EmailContact[];
  recipientSummary: string | null;
  content: string;
  htmlContent: string | null;
  timestamp: Date;
  formattedTime: string;
  preview: string;
}

function headerBgColor(_isMessageFromCustomer: boolean): BgColorValue {
  return "primary";
}

export const EmailMessageCard = memo(function EmailMessageCard({
  channel,
  message,
  supportTicketId,
  initialReplyMode = "reply",
  isExpanded,
  isLast,
  isReplyOpen,
  onToggleExpand,
  onReply,
  onReplyAll,
  onForward,
  onDiscardDraft,
}: {
  channel: MessageChannel;
  message: FormattedEmailMessage;
  supportTicketId: SupportTicketId;
  initialReplyMode?: ReplyMode;
  isExpanded: boolean;
  isLast?: boolean;
  isReplyOpen?: boolean;
  onToggleExpand: () => void;
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
  onDiscardDraft?: () => void;
}) {
  const showReply = isExpanded && isReplyOpen;
  return (
    <Flex
      bgColor="primary"
      borderBottomWidth="1px"
      borderColor="primary"
      borderLeftWidth="1px"
      borderRightWidth="1px"
      borderStyle="solid"
      bottomLeftRadius={isLast ? "sm" : undefined}
      bottomRightRadius={isLast ? "sm" : undefined}
      dir="column"
      gap="none"
      overflow="hidden"
    >
      <MessageHeader
        cc={message.cc}
        formattedTime={message.formattedTime}
        from={message.from}
        isExpanded={isExpanded}
        isMessageFromCustomer={message.isMessageFromCustomer}
        onToggleExpand={onToggleExpand}
        preview={message.preview}
        senderName={message.senderName}
        to={message.to}
      />
      {isExpanded && (
        <Flex bgColor="primary" dir="column" gap="md" p="lg">
          <MessageContent
            content={message.content}
            htmlContent={message.htmlContent}
          />
          {!showReply && (
            <MessageActions
              onForward={onForward}
              onReply={onReply}
              onReplyAll={onReplyAll}
            />
          )}
        </Flex>
      )}
      {showReply && (
        <Flex bgColor="primary" dir="column" p="xl" pt="none">
          <MessageInput
            channel={channel}
            initialReplyMode={initialReplyMode}
            inReplyToMessageId={message.messageId}
            mode="email"
            onDiscardDraft={onDiscardDraft}
            replyRecipients={{ to: [message.from], cc: message.cc, bcc: [] }}
            supportTicketId={supportTicketId}
            threadId={message.threadId}
          />
        </Flex>
      )}
    </Flex>
  );
});

const MessageHeader = memo(function MessageHeader({
  senderName,
  isMessageFromCustomer,
  from,
  to,
  cc,
  formattedTime,
  preview,
  isExpanded,
  onToggleExpand,
}: {
  senderName: string;
  isMessageFromCustomer: boolean;
  from: EmailContact;
  to: EmailContact[];
  cc: EmailContact[];
  formattedTime: string;
  preview: string;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const [mouseDownLoc, setMouseDownLoc] = useState<
    { x: number; y: number } | undefined
  >();

  return (
    <Flex
      align="center"
      bgColor={headerBgColor(isMessageFromCustomer)}
      cursor="pointer"
      gap="md"
      hoverColor="primary"
      justify="space-between"
      onMouseDown={(e) => {
        setMouseDownLoc({ x: e.clientX, y: e.clientY });
      }}
      onMouseUp={(e) => {
        if (
          mouseDownLoc &&
          Math.abs(e.clientX - mouseDownLoc.x) < 5 &&
          Math.abs(e.clientY - mouseDownLoc.y) < 5
        ) {
          onToggleExpand();
        }
      }}
      overflow="hidden"
      p="xl"
    >
      <Flex align="flex-start" gap="md" grow={1} minW="none" overflow="hidden">
        <SenderAvatar senderName={senderName} />
        <Flex dir="column" gap="none" grow={1} minW="none">
          <Text
            fontSize="sm"
            fontWeight="semibold"
            textColor="primary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {senderName}
          </Text>
          {!isExpanded && (
            <Text
              fontSize="xs"
              textColor="secondary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {preview}
            </Text>
          )}
          {isExpanded && (
            <Flex align="center" gap="xs" minW="none">
              <Text
                fontSize="xs"
                textColor="tertiary"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {formatRecipientLine(to, cc)}
              </Text>
              <Tooltip
                placement="bottom-start"
                title={
                  <EmailAddressDetails
                    cc={cc}
                    formattedTime={formattedTime}
                    from={from}
                    to={to}
                  />
                }
              >
                <Flex
                  align="center"
                  onMouseUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  shrink={0}
                  style={{
                    color: "var(--redo-colors-foreground-fg-disabled)",
                    height: 16,
                    width: 16,
                  }}
                >
                  <InfoCircleIcon />
                </Flex>
              </Tooltip>
            </Flex>
          )}
        </Flex>
      </Flex>
      <Text
        fontSize="xs"
        style={{ flexShrink: 0 }}
        textColor="tertiary"
        whiteSpace="nowrap"
      >
        {formattedTime}
      </Text>
    </Flex>
  );
});

const SenderAvatar = memo(function SenderAvatar({
  senderName,
}: {
  senderName: string;
}) {
  return (
    <UserImage
      alt={`${senderName} avatar`}
      imageUrl={null}
      name={senderName}
      size={UserImageSize.SMALL}
    />
  );
});

function formatContactDisplay(contact: EmailContact): string {
  if (contact.name) {
    return `${contact.name} <${contact.email}>`;
  }
  return contact.email;
}

function formatRecipientLine(to: EmailContact[], cc?: EmailContact[]): string {
  const parts: string[] = [];
  if (to.length > 0) {
    parts.push(`to: ${to.map((a) => a.email).join(", ")}`);
  }
  if (cc && cc?.length > 0) {
    parts.push(`cc: ${cc.map((a) => a.email).join(", ")}`);
  }
  return parts.join(", ");
}

function EmailAddressDetails({
  from,
  to,
  cc,
  formattedTime,
}: {
  from: EmailContact;
  to: EmailContact[];
  cc: EmailContact[];
  formattedTime: string;
}) {
  return (
    <Flex dir="column" gap="md">
      <TooltipRow label="From" value={formatContactDisplay(from)} />
      {to.length > 0 && (
        <TooltipRow
          label="To"
          value={to.map(formatContactDisplay).join(", ")}
        />
      )}
      {cc.length > 0 && (
        <TooltipRow
          label="CC"
          value={cc.map(formatContactDisplay).join(", ")}
        />
      )}
      <TooltipRow label="Date" value={formattedTime} />
    </Flex>
  );
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex dir="column" gap="none">
      <Text
        fontSize="xs"
        style={{ color: "var(--redo-colors-text-text-quaternary-500)" }}
      >
        {label}
      </Text>
      <Text fontSize="xs">{value}</Text>
    </Flex>
  );
}

const MessageContent = memo(function MessageContent({
  content,
  htmlContent,
}: {
  content: string;
  htmlContent: string | null;
}) {
  return (
    <Flex dir="column" ml="4xl">
      <SimpleEmailMessage
        messageContent={
          htmlContent
            ? { type: "messageHtml", messageHtml: htmlContent }
            : { type: "messageText", messageText: content || "(No content)" }
        }
      />
    </Flex>
  );
});

const MessageActions = memo(function MessageActions({
  onReply,
  onReplyAll,
  onForward,
}: {
  onReply?: () => void;
  onReplyAll?: () => void;
  onForward?: () => void;
}) {
  return (
    <Flex gap="md" ml="5xl" pb="md" pt="lg">
      <RedoButton
        hierarchy="secondary"
        IconLeading={FlipBackwardIcon}
        onClick={onReply}
        size="sm"
        text="Reply"
      />
      <RedoButton
        hierarchy="secondary"
        IconLeading={CornerUpLeftDoubleIcon}
        onClick={onReplyAll}
        size="sm"
        text="Reply all"
      />
      <RedoButton
        hierarchy="secondary"
        IconLeading={FlipForwardIcon}
        onClick={onForward}
        size="sm"
        text="Forward"
      />
    </Flex>
  );
});
